import { useParams } from 'react-router-dom';
import { AssistantSpan, AuditOperation } from '../Services/TaskScheduleApiProviderTypes';
import { formatDateTimeLocal } from '../utils/formatDate';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { stripHtmlAndTruncate } from '../utils/stripHtmlAndTruncate';
import './AgentAuditOperationDetail.css';
import { buildTrace, TraceRow } from './AgentAuditOperationDetailHelpers';
import AvailableTools from './AvailableTools';
import Button from './Buttons';
import Table from './Tables';

interface AgentAuditOperationDetailProps<T extends AuditOperation> {
  agentAudit: T | null;
  onClose?: () => void;
  showId?: boolean;
}

interface AgentTraceAction {
  label: string;
  url: string;
}

interface AgentTraceRow extends TraceRow {
  actions: AgentTraceAction[];
}

function AgentTraceExpandedRow({ data }: { data: AgentTraceRow }) {
  return (
    <div className="table-folded-content">
      {data.actions.length > 0 && (
        <div className="agent-trace-actions">
          {data.actions.map((action) => (
            <Button
              key={action.url}
              variant="secondary"
              size="small"
              onClick={() => window.open(action.url, '_blank', 'noopener,noreferrer')}
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}
      <div
        dangerouslySetInnerHTML={{
          __html: sanitizeHtml(data.description, { allowInlineStyles: true }),
        }}
      />
    </div>
  );
}

function AgentAuditOperationDetail({
  agentAudit,
  onClose,
  showId = false,
}: AgentAuditOperationDetailProps<AuditOperation>) {
  const { organizationId } = useParams();
  const columns = [
    { name: 'Type', selector: (row: TraceRow) => row.type, sortable: false, width: '120px' },
    {
      name: 'Content',
      selector: (row: TraceRow) => stripHtmlAndTruncate(row.description),
      sortable: false,
    },
  ];

  const availableTools = agentAudit?.available_tools || agentAudit?.trace?.available_tools || [];
  const spans = agentAudit?.spans || [];
  const spanIds = new Set(spans.map((span) => span.span_id));
  const agentSpans = spans.filter((span) => span.operation_name === 'invoke_agent');
  // A2A tools run a nested agent whose invoke_agent span ends first, so prefer the root one.
  const agentSpan =
    agentSpans.find((span) => !span.parent_span_id || !spanIds.has(span.parent_span_id)) ||
    agentSpans[0];
  const toolSpansByCallId = new Map<string, AssistantSpan>();
  const llmSpansByToolCallId = new Map<string, AssistantSpan>();
  const llmSpans: AssistantSpan[] = [];
  let precedingLlmSpan: AssistantSpan | undefined;
  const agentChildSpans = spans
    .filter((span) => span.parent_span_id === agentSpan?.span_id)
    .sort((a, b) => (a.started_at || '').localeCompare(b.started_at || ''));
  for (const span of agentChildSpans) {
    if (span.operation_name === 'chat') {
      precedingLlmSpan = span;
      llmSpans.push(span);
      continue;
    }

    if (span.tool_call_id) {
      toolSpansByCallId.set(span.tool_call_id, span);
      if (precedingLlmSpan) {
        llmSpansByToolCallId.set(span.tool_call_id, precedingLlmSpan);
      }
    }
  }

  const traceRows = buildTrace(agentAudit?.trace_messages);

  // Every LLM call of the run answers with one AI message, so AI rows and chat
  // spans pair up in order. They are paired from the end because the trace
  // can open with history from earlier runs that has no spans here. A row
  // whose tool calls name its span re-anchors the pairing, so a call that left
  // no message (a failure, a barge-in) shifts only the rows before it.
  const llmSpansByTraceRowIndex = new Map<number, AssistantSpan>();
  let llmSpanIndex = llmSpans.length - 1;
  for (let traceRowIndex = traceRows.length - 1; traceRowIndex >= 0; traceRowIndex--) {
    const traceRow = traceRows[traceRowIndex];
    if (traceRow.type !== 'ai') {
      continue;
    }

    const correlatedLlmSpan = traceRow.toolCallIds
      .map((toolCallId) => llmSpansByToolCallId.get(toolCallId))
      .find((span) => span !== undefined);
    if (correlatedLlmSpan) {
      llmSpanIndex = llmSpans.indexOf(correlatedLlmSpan);
    }

    if (llmSpanIndex < 0) {
      break;
    }

    llmSpansByTraceRowIndex.set(traceRowIndex, llmSpans[llmSpanIndex]);
    llmSpanIndex--;
  }

  // A voice session records its speech once for the whole run: synthesis
  // voiced every agent reply, transcription produced every user message.
  const speechOperationIds = agentAudit?.llm_operation_ids_by_usage_type || {};
  const synthesisOperationIds = speechOperationIds.speech_synthesis || [];
  const transcriptionOperationIds = speechOperationIds.audio_transcription || [];

  const agentTraceRows: AgentTraceRow[] = traceRows.map((traceRow, traceRowIndex) => {
    const llmOperationIds = llmSpansByTraceRowIndex.get(traceRowIndex)?.llm_operation_ids || [];
    const speechActions =
      traceRow.type === 'ai'
        ? synthesisOperationIds.map((operationId) => ({
            label: 'Navigate to TTS request',
            operationId,
          }))
        : traceRow.type === 'human'
          ? transcriptionOperationIds.map((operationId) => ({
              label: 'Navigate to STT request',
              operationId,
            }))
          : [];

    const toolCallIds = [
      ...traceRow.toolCallIds,
      ...(traceRow.toolCallId ? [traceRow.toolCallId] : []),
    ];
    const connectionOperationIds = toolCallIds.flatMap(
      (toolCallId) => toolSpansByCallId.get(toolCallId)?.connection_operation_ids || [],
    );
    const auditPath = `/organizations/${organizationId}/audit-operations`;
    const actions = organizationId
      ? [
          ...llmOperationIds.map((operationId) => ({
            label: 'Navigate to LLM request',
            url: `${auditPath}/llm-requests/${operationId}`,
          })),
          ...speechActions.map(({ label, operationId }) => ({
            label,
            url: `${auditPath}/llm-requests/${operationId}`,
          })),
          ...connectionOperationIds.map((operationId) => ({
            label: 'Navigate to tool',
            url: `${auditPath}/connection-requests/${operationId}`,
          })),
        ]
      : [];

    return {
      ...traceRow,
      actions,
    };
  });

  return (
    <div className="d-flex flex-column h-100">
      <h2 className="h4-title">Agent audit operations</h2>

      <div style={{ flexGrow: 1, paddingTop: '1rem' }}>
        {showId && (
          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Id</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <p>{agentAudit?.id}</p>
            </div>
          </div>
        )}
        <div className="input-block d-flex flex-row justify-between">
          <div className="input-description">
            <div className="input-title">Duration</div>
          </div>
          <div className="align-wrapper d-flex align-center">
            <p>{agentAudit?.duration}</p>
          </div>
        </div>
        <div className="input-block d-flex flex-row justify-between">
          <div className="input-description">
            <div className="input-title">Status</div>
          </div>
          <div className="align-wrapper d-flex align-center">
            <p>{agentAudit?.finish_reason || '-'}</p>
          </div>
        </div>
        <div className="input-block d-flex flex-row justify-between">
          <div className="input-description">
            <div className="input-title">Created At</div>
          </div>
          <div className="align-wrapper d-flex align-center">
            <p>{formatDateTimeLocal(agentAudit?.created_at)}</p>
          </div>
        </div>

        <AvailableTools tools={availableTools} />

        <Table
          title=""
          columns={columns}
          data={agentTraceRows}
          expandableRows={true}
          expandableRowsComponent={AgentTraceExpandedRow}
          dense={false}
          noTableHead={false}
          pagination={false}
        />
      </div>
    </div>
  );
}

export default AgentAuditOperationDetail;
